import {
  AlertTriangle,
  FileText,
  MoveDown,
  Plus,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  sq,
  translateQuotationStatus,
  resolveBackendVersionNo,
  resolveLatestBackendVersionNo,
  inputClass,
} from "./quotationUtils";


function STATUS_STYLES({ tone }) {
  const map = {
    draft: "bg-[#eef2f8] text-[#586c8f]",
    pending: "bg-[#ffefbf] text-[#d78600]",
    approved: "bg-[#dde8ff] text-[#2743a0]",
    sent: "bg-[#e8f4ff] text-[#1d7dc2]",
    negotiation: "bg-[#f5ebff] text-[#7d4cbc]",
    accepted: "bg-[#dff8e8] text-[#1f9b56]",
    rejected: "bg-[#fde8e5] text-[#c74b40]",
    superseded: "bg-[#eef2f8] text-[#7b8faa]",
  };
  return map[tone] || map.draft;
}

function StatusPill({ tone, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-[14px] px-3 py-1.5 text-[12px] font-semibold ${STATUS_STYLES({ tone })}`}>
      <span className="h-2 w-2 rounded-full bg-current opacity-95" />
      {translateQuotationStatus(label)}
    </span>
  );
}

function SectionShell({ title, children, action, compact = false }) {
  return (
    <section className={`overflow-hidden border border-[#d8e0ed] bg-white shadow-[0_14px_30px_-26px_rgba(30,51,131,0.28)] ${compact ? "rounded-[12px]" : "rounded-[14px]"}`}>
      <div className={`flex items-center justify-between border-b border-[#d9e1ee] ${compact ? "px-4 py-2.5 sm:px-5" : "px-5 py-4 sm:px-6"}`}>
        <h3 className={`font-semibold tracking-[-0.02em] text-[#2d394c] ${compact ? "text-[13px]" : "text-[15px]"}`}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function QuotationPrimaryButton({ children, onClick, icon: Icon = Plus, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[46px] items-center justify-center gap-2.5 rounded-[12px] bg-[#243c93] px-5 text-[13px] font-semibold text-white shadow-[0_14px_28px_-20px_rgba(36,60,147,0.8)] transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:bg-[#a6b3d6] sm:px-6 sm:text-[14px]"
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <span>{children}</span>
    </button>
  );
}

function QuotationSecondaryButton({ children, onClick, icon: Icon = FileText, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-[12px] border border-[#d0d9ea] bg-white px-4 text-[13px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff] disabled:cursor-not-allowed disabled:text-[#8da0be]"
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <span>{children}</span>
    </button>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-3 px-5 py-5">
      {[0, 1, 2, 3].map((row) => (
        <div key={row} className="grid grid-cols-9 gap-3">
          {Array.from({ length: 9 }).map((_, column) => (
            <div key={column} className="h-10 animate-pulse rounded-[10px] bg-[#edf1f7]" />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <div className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-[#EDF2F8] text-[#2944a8]">
        <FileText className="h-6 w-6" />
      </div>
      <h3 className="text-[18px] font-semibold text-[#23324c]">{sq("emptyTitle")}</h3>
      <p className="mt-2 max-w-xl text-[13px] leading-6 text-[#7184a3]">
        {sq("emptyHint")}
      </p>
      <div className="mt-5">
        <QuotationPrimaryButton onClick={onCreate}>{sq("createQuotation")}</QuotationPrimaryButton>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div className="mx-auto mb-4 flex items-start justify-between gap-4 rounded-[14px] border border-[#f4cfcb] bg-[#fff6f5] px-5 py-4 text-[#b04d43]">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="text-[13px] font-semibold">{sq("errorTitle")}</p>
          <p className="mt-1 text-[12px] leading-5">{message}</p>
        </div>
      </div>
      <button type="button" onClick={onRetry} className="text-[12px] font-semibold text-[#2944a8]">
        {sq("retry")}
      </button>
    </div>
  );
}

function FormField({ label, required = false, error, hint, children, fullWidth = false }) {
  return (
    <div className={fullWidth ? "sm:col-span-2" : ""}>
      <div className="mb-1.5 flex items-center gap-1.5">
        <label className="text-[12px] font-semibold text-[#51657f]">{label}</label>
        {required && <span className="text-[12px] text-[#c74b40]">*</span>}
      </div>
      {children}
      {error ? <p className="mt-1.5 text-[11px] font-medium text-[#c74b40]">{error}</p> : hint ? <p className="mt-1.5 text-[11px] text-[#8ea0bd]">{hint}</p> : null}
    </div>
  );
}

function ComplaintIdInfiniteSelect({
  value,
  onChange,
  options,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  disabled = false,
  hasError = false,
  placeholder = "Select complaint ID",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  function handleScroll(event) {
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 24) {
      onLoadMore?.();
    }
  }

  const selectedLabel = options.find((item) => item.id === value)?.label || value || "";

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={`${inputClass(hasError)} flex items-center justify-between text-left disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#9aa9c0]`}
      >
        <span className={selectedLabel ? "text-[#2d394c]" : "text-[#96a5bb]"}>
          {loading && !options.length
            ? sq("loadingComplaints")
            : selectedLabel || (options.length ? placeholder : sq("noComplaintsAvailable"))}
        </span>
        <MoveDown className={`h-4 w-4 shrink-0 text-[#8ea0bd] transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 overflow-hidden rounded-[12px] border border-[#d1d9e8] bg-white shadow-[0_18px_36px_-24px_rgba(30,51,131,0.35)]">
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-56 overflow-y-auto overscroll-contain py-1"
          >
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="block w-full px-4 py-2.5 text-left text-[13px] text-[#96a5bb] hover:bg-[#f5f8fd]"
            >
              {placeholder}
            </button>
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(option.id);
                  setOpen(false);
                }}
                className={`block w-full px-4 py-2.5 text-left text-[13px] hover:bg-[#f5f8fd] ${
                  value === option.id ? "bg-[#eef3ff] font-semibold text-[#2944a8]" : "text-[#2d394c]"
                }`}
              >
                {option.label}
              </button>
            ))}
            {(loadingMore || (hasMore && options.length > 0)) && (
              <div className="px-4 py-2 text-[11px] font-medium text-[#8ea0bd]">
                {loadingMore ? sq("loadingComplaints") : "Scroll for more"}
              </div>
            )}
            {!hasMore && options.length > 0 && !loadingMore && (
              <div className="px-4 py-2 text-[11px] text-[#a0afc4]">All complaint IDs loaded</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerInfiniteSelect({
  value,
  onChange,
  options = [],
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  disabled = false,
  hasError = false,
  placeholder = "Select customer",
  addNewLabel = "",
  addNewValue = "",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  function handleScroll(event) {
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 24) {
      onLoadMore?.();
    }
  }

  const selectedLabel = options.find((item) => item.name === value)?.name || value || "";

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((current) => !current)}
        className={`${inputClass(hasError)} appearance-none pr-10 text-left disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#9aa9c0]`}
      >
        <span className={`block truncate ${selectedLabel ? "text-[#2d394c]" : "text-[#96a5bb]"}`}>
          {loading && !options.length ? "Loading..." : selectedLabel || placeholder}
        </span>
      </button>
      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-[#8ea0bd]">
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 overflow-hidden rounded-[12px] border border-[#d1d9e8] bg-white shadow-[0_18px_36px_-24px_rgba(30,51,131,0.35)]">
          <div onScroll={handleScroll} className="max-h-56 overflow-y-auto overscroll-contain py-1">
            <button
              type="button"
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
              className="block w-full px-4 py-2.5 text-left text-[13px] text-[#96a5bb] hover:bg-[#f5f8fd]"
            >
              {placeholder}
            </button>
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(option.name);
                  setOpen(false);
                }}
                className={`block w-full px-4 py-2.5 text-left text-[13px] hover:bg-[#f5f8fd] ${
                  value === option.name ? "bg-[#eef3ff] font-semibold text-[#2944a8]" : "text-[#2d394c]"
                }`}
              >
                {option.name}
              </button>
            ))}
            {addNewLabel && addNewValue ? (
              <button
                type="button"
                onClick={() => {
                  onChange(addNewValue);
                  setOpen(false);
                }}
                className="block w-full px-4 py-2.5 text-left text-[13px] font-semibold text-[#2944a8] hover:bg-[#f5f8fd]"
              >
                {addNewLabel}
              </button>
            ) : null}
            {(loadingMore || (hasMore && options.length > 0)) && (
              <div className="px-4 py-2 text-[11px] font-medium text-[#8ea0bd]">
                {loadingMore ? "Loading..." : "Scroll for more"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, helper, value, highlight = false }) {
  return (
    <div className="flex items-center justify-between border-b border-[#edf1f7] py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-center gap-1.5">
        <p className="text-[12px] text-[#6f82a1]">{label}</p>
        {helper && <span className="text-[10px] text-[#9aacbf]">({helper})</span>}
      </div>
      {highlight ? (
        <span className="rounded-full bg-[#fde8e5] px-3 py-1 text-[12px] font-semibold text-[#ca3c2f]">{value}</span>
      ) : (
        <p className="text-[13px] font-semibold text-[#23324c]">{value}</p>
      )}
    </div>
  );
}

function VersionHistoryDrawer({
  open,
  versions,
  latestVersionNo,
  viewingVersionNo,
  viewingId,
  loading = false,
  error = "",
  onClose,
  onSelectVersion,
}) {
  if (!open) return null;

  const resolvedLatest =
    latestVersionNo != null && latestVersionNo !== ""
      ? Number(latestVersionNo)
      : resolveLatestBackendVersionNo(versions);

  return (
    <div className="fixed inset-0 z-120">
      <div className="absolute inset-0 bg-[#0b1430]/35" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-[380px] border-l border-[#d8e0ed] bg-white shadow-[-16px_0_32px_-24px_rgba(30,51,131,0.28)]">
        <div className="flex items-center justify-between border-b border-[#d9e1ee] px-4 py-3">
          <p className="text-[14px] font-semibold text-[#23324c]">Version History</p>
          <button type="button" onClick={onClose} className="rounded-lg border border-[#d8e0ed] px-2.5 py-1 text-[11px] font-semibold text-[#5d7598]">
            Close
          </button>
        </div>
        <div className="space-y-2 px-3 py-3">
          {error ? (
            <p className="px-1 py-3 text-[12px] text-[#b04d43]">{error}</p>
          ) : null}
          {loading && versions.length === 0 ? (
            <p className="px-1 py-3 text-[12px] text-[#8ea0bd]">Loading versions...</p>
          ) : versions.length === 0 && !error ? (
            <p className="px-1 py-3 text-[12px] text-[#8ea0bd]">No version history available.</p>
          ) : (
            versions.map((version) => {
              const versionNo = resolveBackendVersionNo(version);
              const isLatest =
                resolvedLatest != null &&
                versionNo != null &&
                Number(versionNo) === Number(resolvedLatest);
              const isViewing =
                (viewingId != null &&
                  viewingId !== "" &&
                  version.id != null &&
                  Number(version.id) === Number(viewingId)) ||
                (viewingVersionNo != null &&
                  versionNo != null &&
                  Number(versionNo) === Number(viewingVersionNo));
              return (
                <button
                  key={`${version.refId}-${versionNo ?? version.version}-${version.id ?? ""}`}
                  type="button"
                  onClick={() => onSelectVersion?.(version)}
                  className={`block w-full rounded-[10px] border px-3 py-2.5 text-left transition hover:border-[#9eb3dc] hover:bg-[#f7faff] ${
                    isViewing ? "border-[#c6d4f0] bg-[#f7faff]" : "border-[#e3e9f3] bg-white"
                  }`}
                >
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                    <span className="font-semibold text-[#23324c]">{version.versionLabel}</span>
                    <span className="text-[#c5d0e0]">·</span>
                    <span className="text-[#7d8fa9]">Updated {version.updatedLabel}</span>
                    <span className="text-[#c5d0e0]">·</span>
                    <StatusPill tone={version.statusTone} label={version.status} />
                    {version.superseded && <StatusPill tone="superseded" label="Superseded" />}
                    {isLatest && <StatusPill tone="approved" label="Current" />}
                    {isViewing && !isLatest && <StatusPill tone="pending" label="Viewing" />}
                  </div>
                </button>
              );
            })
          )}
          {loading && versions.length > 0 ? (
            <p className="px-1 pt-1 text-[11px] text-[#8ea0bd]">Refreshing versions...</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function QuotationConfirmModal({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirming = false,
  tone = "danger",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  const confirmClass =
    tone === "primary"
      ? "inline-flex min-h-[46px] items-center justify-center gap-2.5 rounded-[12px] bg-[#243c93] px-5 text-[13px] font-semibold text-white shadow-[0_14px_28px_-20px_rgba(36,60,147,0.8)] transition hover:bg-[#1d327f] disabled:cursor-not-allowed disabled:bg-[#a6b3d6] sm:px-6 sm:text-[14px]"
      : "inline-flex min-h-[46px] items-center justify-center gap-2.5 rounded-[12px] bg-[#c7463c] px-5 text-[13px] font-semibold text-white shadow-[0_14px_28px_-20px_rgba(199,70,60,0.55)] transition hover:bg-[#b33c33] disabled:cursor-not-allowed disabled:bg-[#e0a29d] sm:px-6 sm:text-[14px]";

  return (
    <div className="fixed inset-0 z-140">
      <div className="absolute inset-0 bg-[#0b1430]/35" onClick={confirming ? undefined : onCancel} />
      <div className="absolute left-1/2 top-1/2 w-[min(480px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-[24px] border border-[#d8e0ed] bg-white shadow-[0_30px_60px_-30px_rgba(30,51,131,0.35)]">
        <div className="border-b border-[#d9e1ee] px-5 py-4 sm:px-6">
          <p className="text-[18px] font-semibold text-[#23324c]">{title}</p>
          {description ? (
            <p className="mt-1 text-[12px] leading-5 text-[#7c8faa]">{description}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-3 px-5 py-4 sm:px-6">
          <QuotationSecondaryButton icon={null} onClick={onCancel} disabled={confirming}>
            {cancelLabel}
          </QuotationSecondaryButton>
          <button type="button" onClick={onConfirm} disabled={confirming} className={confirmClass}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuotationImportFailureDialog({ open, reason, onOpenChange, onCreateAgain }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[150] bg-[#0b1430]/35" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-[151] w-[min(480px,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-[24px] border border-[#d8e0ed] bg-white p-6 shadow-xl">
          <DialogPrimitive.Title className="text-lg font-semibold text-[#23324c]">
            Import Failed
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-3 max-h-[50vh] overflow-y-auto whitespace-pre-wrap break-words text-sm text-[#526581]">
            {reason || "The quotation import failed. No failure reason was provided."}
          </DialogPrimitive.Description>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <DialogPrimitive.Close asChild>
              <button type="button" className="rounded-xl border border-[#d8e0ed] px-5 py-3 text-sm font-semibold text-[#23324c]">Close</button>
            </DialogPrimitive.Close>
            <QuotationPrimaryButton onClick={onCreateAgain}>
              Create Quotation Again
            </QuotationPrimaryButton>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export {
  ComplaintIdInfiniteSelect,
  CustomerInfiniteSelect,
  EmptyState,
  ErrorState,
  FormField,
  QuotationConfirmModal,
  QuotationImportFailureDialog,
  QuotationPrimaryButton,
  QuotationSecondaryButton,
  SectionShell,
  StatusPill,
  SummaryRow,
  TableSkeleton,
  VersionHistoryDrawer
};
